import {Injectable} from '@angular/core';

import * as _ from 'lodash';
import {AcDialogRef, ConfirmDialogData, RefreshTableProperties} from 'ac-infra';
import {AlarmsRestService} from '../apis/alarms-rest.service';
import {TenantsRestService} from '../../network/services/apis/tenants-rest.service';
import {SaveDataToFileService} from '../../common/utilities/save-data-to-file.service';
import {RestResponseSuccess} from '../../common/server-actions/rest';
import {Actions} from '../../common/server-actions/actions';
import {AlarmsForwardingDialogComponent} from '../dialogs/alarms-forwarding-dialog/alarms-forwarding-dialog.component';
import {Subject} from 'rxjs';
import {AuthorizationService} from '../../common/services/authorization.service';
import {ServerInfoService} from '../../common/services/server-info.service';
import {AlarmsFilterFormatterService} from './alarms-filter-formatter.service';
import {ConstantStringsService} from "../../common/utilities/constant-strings.service";
import {
    ExternalApplicationsRestService
} from "../../system/configuration/configuration-api/external-applications-rest.service";


@Injectable({providedIn: 'root'})
export class AlarmsActionsService extends Actions {

    static readonly ALARM_EVENT_TYPE = 'Alarm';
    static readonly JOURNAL_EVENT_TYPE = 'Journal';

    alarmsFWDEmpty = require('../dialogs/alarms-forwarding-dialog/alarmsfwd.empty.json');
    journalsFWDEmpty = require('../dialogs/alarms-forwarding-dialog/journalsfwd.empty.json');
    ACTIVE_ALARMS_SHARED_FIELDS = ['alarmSeverity', 'alarmReceivedTime', 'unitName', 'alarmSource', 'alarmSourceDescription', 'name',
        'alarmTextualDescription', 'alarmDateAndTime', 'alarmUniqId', 'alarmType', 'alarmProbCause', 'alarmAddInfo1',
        'alarmAddInfo2', 'alarmAddInfo3', 'isEvent', 'id', 'unitId', 'alarmRemoteHost', 'alarmOID', 'alarmSystemUpTime',
        'alarmRemotePort', 'alarmSNMPCommunity', 'alarmProtocol', 'url', 'alarmStatus', 'alarmStatusLastUpdateTime', 'alarmOperator'
    ];
    JOURNAL_ACTIVITIES_TPL_OBJECT = [
        'createDateTime', 'source', 'name',
        'description', 'unitId', 'unitType',
        'tenantId', 'regionId', 'siteId',
        'operator', 'operatorIpAddress', 'id', 'url'
    ];

    severityMap = {
        CRITICAL: 'severity-critical',
        MAJOR: 'severity-major',
        MINOR: 'severity-minor',
        WARNING: 'severity-warning',
        INDETERMINATE: 'severity-indeterminate',
        CLEARED: 'severity-clear',
        JOURNAL: 'severity-journal'
    };

    alarmsSourceTypes: any = {
        device: 'Devices',
        site: 'Site',
        link: ConstantStringsService.linkOrProviderSide,
        endpoint: 'Endpoint',
        arm: 'ARM',
        zoom: 'UCaaS Connect',
        voca: 'VOCA CIC',
        meetingInsights: 'Meeting Insights',
        interactionRecording: 'Interaction Insights',
    };

    ACTIVE_AND_HISTORY_ALARMS_TPL_OBJECT = [...this.ACTIVE_ALARMS_SHARED_FIELDS, 'siteId', 'regionName', 'regionId', 'tenantId'];

    private forwardingRulesTableRefreshSubject: Subject<RefreshTableProperties> = new Subject<RefreshTableProperties>();
    forwardingRulesTableRefresh$ = this.forwardingRulesTableRefreshSubject.asObservable();

    constructor(private alarmsRestService: AlarmsRestService,
                private alarmsFilterFormatterService: AlarmsFilterFormatterService,
                private tenantsRestService: TenantsRestService,
                private saveDataToFileService: SaveDataToFileService,
                private externalApplicationsRestService: ExternalApplicationsRestService,
                private authorizationService: AuthorizationService) {
        super({entityName: 'alarm', entityService: alarmsRestService, throttledUrlPart: 'alarms/statistics'});
        if (this.authorizationService.validForMonitorLinkOrOneLiveTenantUserOrGroupUsers()) {
            this.ACTIVE_AND_HISTORY_ALARMS_TPL_OBJECT = this.ACTIVE_ALARMS_SHARED_FIELDS;
        }

        if (ServerInfoService.oneLiveMode) {
            this.alarmsSourceTypes.zoom = 'UCaaS Connect';
            this.alarmsSourceTypes.service = 'Services';
            this.alarmsSourceTypes.lcCustomer = 'Customers';
        }
    }

    executeForwardingRulesTableRefreshSubject = (tableProperties: RefreshTableProperties) => {
        this.forwardingRulesTableRefreshSubject.next({...tableProperties, entityName: 'Forwarding Rule Table'});
    };

    acknowledgeActiveAlarms = (selection) => {
        const serverCallback = (onSuccess, onFailure) => this.alarmsRestService.acknowledgeActiveAlarms(onSuccess, onFailure, selection);
        const dialogData: ConfirmDialogData = {entitiesArray: selection};

        this.genericConfirmAction({serverCallback, dialogData, dialogConfig: {submitButtonText: 'acknowledge'}});
    };

    deleteActiveAlarms = (selection) => {
        const serverCallback = (onSuccess, onFailure) => this.alarmsRestService.clearActiveAlarms(onSuccess, onFailure, selection);
        const dialogData: ConfirmDialogData = {entitiesArray: selection};
        this.genericConfirmAction({serverCallback, dialogData, dialogConfig: _.cloneDeep(this.deleteDialogConfig)});
    };

    saveAllAlarmsToFile = (moduleName) => {
        const moduleFilter = this.alarmsFilterFormatterService.getFilterParameter(moduleName, 'AlarmsFilter', false);
        const moduleFilterDescription = this.alarmsFilterFormatterService.getFilterParameter(moduleName, 'AlarmsFilter', true);
        const parameters = {filter: moduleFilter, sort: null, pager: {limit: 1000}};

        const message = this.buildInfoMessage(moduleName);
        this.acDialogService.info(message);

        const success = (value: RestResponseSuccess) => this.saveAlarmsToFile(moduleName, moduleFilterDescription, value.data);
        this.alarmsRestService.getAlarms(moduleName, {
            success,
            parameters
        });
    };

    editAlarmsForwardingRule = (fwdRuleId) => {
        const dialogRef = this.acDialogService.open();
        this.alarmsRestService.getAlarmsForwardingRule(fwdRuleId, {
            success: (response: RestResponseSuccess) => {
                this.openAlarmsForwardingRuleDialog(response.data, undefined, dialogRef)
            }
        });
    };

    openAlarmsForwardingRuleDialog = (rule = undefined, ruleType = undefined, dialogRef?: AcDialogRef) => {
        if (!dialogRef) {
            dialogRef = this.acDialogService.open();
        }

        if(this.authorizationService.isSystemAdminOrOperatorLevel()){
            setTimeout(() => {
                this.externalApplicationsRestService.getApplicationsStatuses((response: RestResponseSuccess) => {
                    this.prepareFWDialog(rule, ruleType, response, dialogRef);
                },() => null);
            })
        }else{
            this.prepareFWDialog(rule, ruleType, undefined, dialogRef);
        }

    };

    prepareFWDialog = (rule, ruleType, externalAppsResponse, dialogRef) => {
        const isEdit = !!rule;
        rule = rule || {};
        rule.ruleType = rule.ruleType || ruleType;
        const fwdRule = _.cloneDeep(isEdit ? rule : (rule.ruleType === AlarmsActionsService.ALARM_EVENT_TYPE ? this.alarmsFWDEmpty : this.journalsFWDEmpty));
        const id = fwdRule.id;

        let tenants;
        if (fwdRule.ruleType === AlarmsActionsService.ALARM_EVENT_TYPE) {
            tenants = this.tenantsRestService.getAllEntities();
            tenants = _.orderBy(tenants, [tenant => tenant.name.toLowerCase()], ['asc']);
            if (this.authorizationService.isSystemLevel()) {
                tenants.unshift({id: -1, name: 'System - all tenants'});
            } else if (!isEdit) {
                fwdRule.tenantId = tenants?.length > 0 ? tenants[0].id : undefined;
            }
        }

        const onSuccessCallback = () => {
            this.executeForwardingRulesTableRefreshSubject({showLoader: true});
        };

        const dialogData: any = {entity: fwdRule, tenants, isEdit, onSuccessCallback};

        if(externalAppsResponse){
            dialogData.externalApps = externalAppsResponse.data?.['external applications']?.filter((app) => !['REPORTS', 'IPPMANAGER'].includes(app.id));
        }

        const serverCallback = (onSuccess, onFailure) => {
            if(this.authorizationService.isTenantUser()){// gal and yehudit request, tenant and region are not relevant for SP, and if its not empty its effecting the calc in the server side
                fwdRule.alarmTenantsFilter = [];
                fwdRule.alarmRegionsFilter = [];
            }

            if (isEdit) {
                this.alarmsRestService.edit(onSuccess, onFailure, fwdRule, id);
            } else {
                this.alarmsRestService.add(onSuccess, onFailure, fwdRule);
            }
        };
        const dialogComponentType = AlarmsForwardingDialogComponent;
        this.genericAction({serverCallback, dialogData, dialogRef, dialogComponentType});
    }

    deleteAlarmsForwardingRule = (fwdRule) => {
        const deleteCallback = () => {
            this.executeForwardingRulesTableRefreshSubject({showLoader: true});
        };
        this.delete({
            entityArray: [fwdRule],
            alternativeEntityName: 'forwarding rule',
            dontPerformRefresh: true,
            deleteCallback,
            isDeleteSingle: true
        });
    };

    saveAlarmsToFile = (moduleName, moduleFilter, value) => {
        const propertyName = moduleName === 'journal' ? 'journal' : 'alarms';
        const defaultTplObject = moduleName === 'journal' ? this.JOURNAL_ACTIVITIES_TPL_OBJECT : this.ACTIVE_AND_HISTORY_ALARMS_TPL_OBJECT;

        this.saveDataToFileService.createCSVFile(moduleName, moduleFilter, value[propertyName], defaultTplObject);
    };

    buildInfoMessage = (moduleName) => {
        const forModule = moduleName === 'journal' ? 'Journal Activities' : 'Alarms and Events';
        return ['Started gathering data for CSV file.\n', 'For module: ' + forModule + '\n'].join('');
    };
}
